import { memo } from "react";
import { Outlet } from "react-router-dom";
import { Loader, PageHolder } from "react-better-html";

import { type SystemUserRole } from "../../types/user";
import { type OrganizationType, type OrganizationUserRole } from "../../types/organization";

import { LocalStorage } from "../../utils/localStorage";
import { decryptJwtToken } from "../../utils/functions";
import { useReduxState, useSelectedOrganization } from "../../utils/hooks";

import Redirect from "./Redirect";

type RestrictedProps = {
   systemUserRoles?: SystemUserRole[];
   organizationTypes?: OrganizationType[];
   organizationUserRoles?: OrganizationUserRole[];
   needSelectedOrganization?: boolean;
   children?: React.ReactNode;
};

function Restricted({
   systemUserRoles,
   organizationTypes,
   organizationUserRoles,
   needSelectedOrganization,
   children,
}: RestrictedProps) {
   const selectedOrganization = useSelectedOrganization();

   const firebaseUser = LocalStorage.getItem("firebaseUser");
   const cachedFirebaseUserIdToken = LocalStorage.getItem("firebaseUserIdToken");
   const cachedUser = LocalStorage.getItem("user");

   const firebaseUserIdToken = useReduxState((state) => state.user.firebaseUserIdToken);
   const selectedOrganizationId = useReduxState((state) => state.organizations.selectedOrganizationId);

   const readyFirebaseIdToken = firebaseUserIdToken ?? cachedFirebaseUserIdToken;
   const decryptedIdToken = readyFirebaseIdToken ? decryptJwtToken(readyFirebaseIdToken) : undefined;
   const waitFirebaseIdTokenRefresh = decryptedIdToken
      ? decryptedIdToken.exp * 1000 < new Date().getTime() - 60 * 1000
      : false;

   const hasRequiredSystemRole = systemUserRoles
      ? cachedUser
         ? systemUserRoles.includes(cachedUser.systemUserRole)
         : false
      : true;
   const hasRequiredOrganizationType =
      organizationTypes && selectedOrganization ? organizationTypes.includes(selectedOrganization.type) : true;

   const hasRequiredOrganizationRole =
      organizationUserRoles && selectedOrganization
         ? organizationUserRoles.includes(selectedOrganization.userRole)
         : true;

   return firebaseUser &&
      cachedUser &&
      hasRequiredSystemRole &&
      hasRequiredOrganizationType &&
      hasRequiredOrganizationRole ? (
      <>
         {!waitFirebaseIdTokenRefresh && (needSelectedOrganization ? selectedOrganizationId !== undefined : true) ? (
            <>{children ?? <Outlet />}</>
         ) : (
            <PageHolder>
               <Loader.box />
            </PageHolder>
         )}
      </>
   ) : (
      <PageHolder>{cachedUser ? <Redirect to="/" /> : <Redirect to="/auth" />}</PageHolder>
   );
}

export default memo(Restricted);
